import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Generate a UUID in a browser-safe way.
 * Uses `crypto.randomUUID()` when available; otherwise falls back to a RFC4122 v4 UUID
 * using `crypto.getRandomValues()`.
 *
 * This avoids runtime crashes on older mobile browsers (notably older iOS Safari).
 */
export function randomUUID(): string {
  const c = globalThis.crypto as Crypto | undefined;

  // Prefer native implementation when present.
  if (typeof c?.randomUUID === "function") {
    return c.randomUUID();
  }

  // Fallback: RFC4122 version 4 using secure random bytes when possible.
  if (typeof c?.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);

    // Per RFC4122 §4.4
    bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40; // version 4
    bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80; // variant 10

    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
      .slice(6, 8)
      .join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
  }

  // Last resort (should be rare): non-crypto randomness.
  return `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx for conditional classes with tailwind-merge for deduplication
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Parse a date from API (string or Date) into a Date.
 * ISO strings without timezone (e.g. "2026-02-11T20:06:00") are treated as UTC.
 */
function parseDate(date: Date | string): Date {
  if (typeof date !== "string") return date;
  const s = date.trim();
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !/Z|[+-]\d{2}:?\d{2}$/.test(s)) {
    return new Date(s + "Z");
  }
  return new Date(s);
}

/**
 * Format a date for display in the current user's timezone (browser local time).
 * Use for "Submitted at" and similar so moderators see times in their own TZ.
 */
export function formatDate(date: Date | string): string {
  const d = parseDate(date);
  if (isNaN(d.getTime())) return "invalid date";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: undefined, // use runtime default = user's local timezone in browser
  }).format(d);
}

/**
 * Format a relative time (e.g., "2 hours ago") based on the current instant.
 * Uses UTC-safe parsing so "X ago" is correct regardless of timezone.
 */
export function formatRelativeTime(date: Date | string | null | undefined): string {
  if (!date) return "unknown";
  const d = parseDate(date);
  if (isNaN(d.getTime())) {
    return "invalid date";
  }
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
  }

  return formatDate(d);
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}

/**
 * Sleep for a specified duration
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Debounce a function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Generate or retrieve a unique device ID for anonymous prayer tracking
 * Used to prevent showing the same prayer repeatedly to a user
 */
export function getOrCreateDeviceId(): string {
  if (typeof window === 'undefined') return '';
  
  const storageKey = 'one-voice-device-id';
  let deviceId = localStorage.getItem(storageKey);
  
  if (!deviceId) {
    deviceId = `device-${randomUUID()}`;
    localStorage.setItem(storageKey, deviceId);
  }
  
  return deviceId;
}
